/// <amd-module name="VSSUI/Components/SuggestionsList/SuggestionsList" />
import * as React from "react";
import { ObservableLike } from "VSS/Core/Observable";
import { FocusWithin, IFocusWithinStatus } from "VSSUI/FocusWithin";
import { Observer } from "VSSUI/Observer";
import { Spinner } from "VSSUI/Spinner";
import { css, getSafeId, preventDefault } from "VSSUI/Util";
import { ISuggestionItemProps, ISuggestionsListProps } from "./SuggestionsList.Props";

export class SuggestionsItem<T> extends React.Component<ISuggestionItemProps<T>> {
    public render(): JSX.Element {
        const { renderSuggestion, className, isSelected } = this.props;
        return (
            <div
                className={css(
                    className,
                    "bolt-suggestions-item flex-row flex-grow cursor-pointer scroll-hidden",
                    isSelected && "bolt-suggestions-isSuggested"
                )}
                onPointerDown={preventDefault}
            >
                <div
                    onMouseDown={preventDefault}
                    onClick={this.onSuggestionClicked}
                    className="bolt-suggestions-item-button flex-row flex-grow scroll-hidden"
                >
                    {renderSuggestion(this.props)}
                </div>
            </div>
        );
    }

    private onSuggestionClicked = (ev: React.MouseEvent<HTMLElement>): void => {
        if (!ev.isDefaultPrevented()) {
            this.props.onClick(this.props);
            ev.preventDefault();
        }
    };
}

export class SuggestionsList<T> extends React.Component<ISuggestionsListProps<T>> {
    protected scrollableRegion = React.createRef<HTMLDivElement>();
    protected selectedElement = React.createRef<HTMLDivElement>();
    private currentSelectedElement: HTMLDivElement | null;

    public render(): JSX.Element {
        const { className, loadingText, renderNoResultFound } = this.props;
        const suggestions = ObservableLike.getValue(this.props.suggestions);
        const isLoading = ObservableLike.getValue(this.props.isLoading);

        // MostRecently Used text should supercede the header text if it's there and available.
        const hasNoSuggestions = (!suggestions || !suggestions.length) && !isLoading;
        return (
            <div
                className={css(className, "bolt-suggestions flex-row flex-grow")}
                id={getSafeId("suggestions-list")}
                style={this.props.width ? { width: this.props.width } : undefined}
                onMouseDown={preventDefault}
            >
                {isLoading ? (
                    <Spinner className="bolt-suggestions-spinner flex-grow flex-center justify-center" id="suggestions-spinner" label={loadingText} />
                ) : (
                    !hasNoSuggestions && this.renderSuggestions()
                )}
                <div
                    className={css("bolt-suggestions-none-region", !hasNoSuggestions ? "has-suggestions" : "flex-row flex-grow")}
                    id={getSafeId("sug-list-no-results")}
                    role="alert"
                >
                    {hasNoSuggestions && (renderNoResultFound ? renderNoResultFound() : this.noResults())}
                </div>
                <div aria-label={loadingText || "Loading"} id={getSafeId("sug-list-transition")} role="text" />
            </div>
        );
    }

    public componentDidMount(): void {
        this.scrollSelected();
    }

    public componentDidUpdate(): void {
        // Scroll to selected element only in case if selected element changed.
        // Otherwise mouse scroll won't work as it doesn't change selected element.
        if (this.selectedElement.current && this.currentSelectedElement !== this.selectedElement.current) {
            this.scrollSelected();
        }
    }

    private noResults(): JSX.Element | null {
        const { noResultsFoundText } = this.props;
        return noResultsFoundText ? <div className="bolt-suggestions-none flex-row flex-grow flex-center">{noResultsFoundText}</div> : null;
    }

    private renderSuggestions(): JSX.Element {
        const {
            createDefaultItem,
            renderSuggestion,
            onSuggestionClicked,
            suggestionsItemClassName,
            resultsMaximumNumber,
            selectedIndex,
            suggestions,
            suggestionsContainerAriaLabel,
            width
        } = this.props;
        return (
            <FocusWithin onBlur={this.onBlur} onFocus={this.onFocus}>
                {(focusStatus: IFocusWithinStatus) => {
                    return (
                        <Observer suggestions={suggestions} selectedIndex={selectedIndex}>
                            {(props: { suggestions: T[]; selectedIndex: number }) => {
                                if (resultsMaximumNumber) {
                                    props.suggestions = props.suggestions.slice(0, resultsMaximumNumber);
                                }

                                const sugList = props.suggestions;
                                createDefaultItem && sugList.unshift(createDefaultItem(sugList));
                                return (
                                    <div
                                        aria-label={suggestionsContainerAriaLabel}
                                        className="bolt-suggestions-container flex-column flex-grow v-scroll-auto h-scroll-hidden"
                                        id={getSafeId("suggestion-list")}
                                        ref={this.scrollableRegion}
                                        role="listbox"
                                        style={width ? { width: width } : undefined}
                                    >
                                        <div className="bolt-suggestion-spacer" onMouseDown={preventDefault} />
                                        {props.suggestions.map((suggestion, index) => {
                                            const isSuggested = index === props.selectedIndex;
                                            return (
                                                <div
                                                    className="flex-noshrink"
                                                    key={getSafeId("sug-" + index)}
                                                    id={getSafeId("sug-row-" + index)}
                                                    ref={isSuggested ? this.selectedElement : ""}
                                                    role="option"
                                                >
                                                    <SuggestionsItem
                                                        className={suggestionsItemClassName}
                                                        id={getSafeId("sug-item" + index)}
                                                        index={index}
                                                        isSelected={isSuggested}
                                                        item={suggestion}
                                                        onBlur={focusStatus.onBlur}
                                                        onFocus={focusStatus.onFocus}
                                                        onClick={onSuggestionClicked}
                                                        renderSuggestion={renderSuggestion}
                                                    />
                                                </div>
                                            );
                                        })}
                                        <div className="bolt-suggestion-spacer" onMouseDown={preventDefault} />
                                    </div>
                                );
                            }}
                        </Observer>
                    );
                }}
            </FocusWithin>
        );
    }

    private onBlur = (): void => {
        this.props.onBlur && this.props.onBlur();
    };

    private onFocus = (event: React.FocusEvent<HTMLElement>): void => {
        this.props.onFocus && this.props.onFocus(event);
    };

    private scrollSelected(): void {
        this.currentSelectedElement = this.selectedElement?.current;

        if (this.scrollableRegion.current && this.selectedElement?.current) {
            // IE11 messes up when scrollIntoView is called in the callout.
            this.scrollableRegion.current.scrollTop = this.selectedElement.current.offsetTop;
        }
    }
}
